import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { fetchProductsPage } from "@/lib/api";
import { MediaTile } from "@/components/MediaTile";
import { Skeleton } from "@/components/ui/skeleton";
import { SectionHeading } from "./SectionHeading";
import { ArrowRight } from "lucide-react";

const CATEGORIES = [
  { category: "gold", label: "Gold Rings" },
  { category: "silver", label: "Silver" },
  { category: "diamond", label: "Diamond" },
  { category: "others", label: "Others" },
];

/**
 * "Shop by Category" strip — each tile shows a real product photo (pulled
 * live from the Admin panel, first active item in that category) with the
 * category name captioned below, matching the reference site's category
 * navigation style. Clicking a tile opens the full filtered catalog page.
 * Falls back to a plain gold tile with just the label if no photo has been
 * uploaded yet for that category.
 */
export const ShopByCategory = () => {
  const [tiles, setTiles] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        CATEGORIES.map(async (c) => {
          try {
            const items = await fetchProductsPage({ category: c.category, limit: 1 });
            return { ...c, item: items?.[0] || null };
          } catch {
            return { ...c, item: null };
          }
        })
      );
      if (!cancelled) setTiles(results);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="relative py-14 sm:py-20 bg-background-alt">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Shop by Category"
          title="Find Your Perfect Match"
          subtitle="Jump straight to what you're looking for."
        />

        {!tiles ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="aspect-square rounded-2xl" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6">
            {tiles.map(({ category, label, item }) => (
              <button
                key={category}
                onClick={() => navigate(`/all-items?category=${category}`)}
                className="group flex flex-col items-center"
                data-testid={`shop-category-${category}`}
              >
                <div className="relative aspect-square w-full rounded-2xl overflow-hidden bg-card border border-border group-hover:border-primary/50 transition-colors duration-300">
                  {item?.media_url ? (
                    <MediaTile
                      src={item.media_url}
                      mediaType={item.media_type}
                      alt={label}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center" style={{ background: "var(--gradient-gold)" }}>
                      <span className="text-primary-foreground/70 text-xs font-medium">No photo yet</span>
                    </div>
                  )}
                </div>
                <span className="mt-3 text-sm sm:text-base font-semibold uppercase tracking-wide text-foreground text-center">
                  {label}
                </span>
              </button>
            ))}

            <Link
              to="/all-items"
              className="group flex flex-col items-center"
              data-testid="shop-category-view-all"
            >
              <div className="relative aspect-square w-full rounded-2xl overflow-hidden bg-card border border-dashed border-primary/40 flex items-center justify-center group-hover:bg-primary/5 transition-colors duration-300">
                <ArrowRight className="h-8 w-8 text-primary group-hover:translate-x-1 transition-transform duration-300" />
              </div>
              <span className="mt-3 text-sm sm:text-base font-semibold uppercase tracking-wide text-primary text-center">
                View All
              </span>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};
